import { supabase } from "@/integrations/supabase/client";
import type { ChainSigner, SigningFile, SigningListItem } from "./types";
import { parseTimeOrZero, sessionItemStatus } from "./status";

/**
 * Signing list repository — reads the rows the Dokobit integration writes.
 *
 * Two sources feed one list:
 *  - `documents` with `source='signing'` — the finished .asice containers, with
 *    their `document_signatures` audit rows embedded.
 *  - `signing_sessions` — in-flight, failed, cancelled or expired chains, with
 *    their `signing_session_signers` rows embedded. Completed ones are dropped
 *    here because their container is already in the first list.
 *
 * Both are scoped to one building AND one project — PLUS that building's
 * unfiled rows (`project_id IS NULL`), which have no project page to appear on
 * and would otherwise be unreachable from a Projects-only page. They come back
 * flagged `unfiled` so the UI can label them instead of implying they belong to
 * the selected project. Asking for the null scope itself returns only those.
 *
 * COLUMN GRANTS ARE EXPLICIT on `signing_sessions`, `signing_session_signers`
 * and `document_signatures` (FDP migrations 20260804065406 / 20260804085221):
 * `select('*')` raises 42501, and `document_signatures.signer_personal_code`
 * plus `signing_session_signers.personal_code` are revoked from `authenticated`
 * outright. Every select below projects granted columns only — do not add one
 * without checking the GRANT.
 */

/**
 * Cap on each list query. All search/filter/sort is client-side, so without an
 * explicit limit PostgREST's max-rows cap would truncate silently and the
 * search box would report "no matches" for rows that exist. Both queries order
 * newest-first, so a full page means "the latest N" — the UI shows a note when
 * a page hits this. ponytail: real paging if a building ever exceeds this.
 */
export const SIGNING_LIST_LIMIT = 500;

interface ProfileNameRow {
  first_name: string | null;
  last_name: string | null;
}

interface ContainerRow {
  id: number;
  project_id: number | null;
  filename: string;
  size: number;
  created_at: string | null;
  created_by: number | null;
  created_by_name: string | null;
  file_path: string | null;
  content_type: string | null;
  state: string | null;
  verified_at: string | null;
  user_profiles: ProfileNameRow | null;
  verified_by_profile: ProfileNameRow | null;
  document_signatures: SignatureRow[] | null;
}

/** A `document_signatures` row, granted columns only (no `signer_personal_code`). */
export interface SignatureRow {
  id: number;
  signer_name: string | null;
  signer_user_id: number | null;
  method: string | null;
  signed_at: string;
}

/** A `signing_session_signers` row, granted columns only. */
export interface SignerRow {
  position: number;
  user_id: number;
  name: string;
  surname: string | null;
  status: string;
  signed_at: string | null;
  signing_method: string | null;
}

interface SessionRow {
  id: string;
  user_id: number;
  project_id: number | null;
  container_name: string;
  status: string;
  error_message: string | null;
  created_at: string;
  expires_at: string | null;
  source_doc_ids: number[] | null;
  container_document_id: number | null;
  user_profiles: ProfileNameRow | null;
  signing_session_signers: SignerRow[] | null;
}

const CONTAINER_COLUMNS =
  "id, project_id, filename, size, created_at, created_by, created_by_name, file_path, " +
  "content_type, state, verified_at, " +
  "user_profiles!documents_created_by_fk(first_name,last_name), " +
  "verified_by_profile:user_profiles!documents_verified_by_fk(first_name,last_name), " +
  "document_signatures(id, signer_name, signer_user_id, method, signed_at)";

const SESSION_COLUMNS =
  "id, user_id, project_id, container_name, status, error_message, created_at, expires_at, " +
  "source_doc_ids, container_document_id, " +
  "user_profiles!signing_sessions_user_id_fkey(first_name,last_name), " +
  "signing_session_signers(position, user_id, name, surname, status, signed_at, signing_method)";

/**
 * The project scope both list queries share: the selected project PLUS the
 * building's unfiled rows.
 *
 * `project_id` is a number here (parsed from the route/scope store, never user
 * text), so it interpolates safely into PostgREST's filter grammar — which has
 * no parameter binding for `or=`.
 */
export function projectScopeFilter(projectId: number): string {
  return `project_id.is.null,project_id.eq.${projectId}`;
}

function fullName(profile: ProfileNameRow | null, fallback: string | null): string {
  const name = `${profile?.first_name ?? ""} ${profile?.last_name ?? ""}`.trim();
  return name || fallback?.trim() || "";
}

/** `signing_session_signers.status` is a CHECK; an unrecognised value reads as pending. */
export function toSignerStatus(value: string): ChainSigner["status"] {
  switch (value) {
    case "signed":
    case "rejected":
    case "removed":
      return value;
    default:
      return "pending";
  }
}

/** Position, then name — a parallel group has no inherent order, so give it a stable one. */
export function byPositionThenName(a: ChainSigner, b: ChainSigner): number {
  return a.position - b.position || a.name.localeCompare(b.name);
}

export function toChainSigner(row: SignerRow, currentUserId: number | null): ChainSigner {
  return {
    position: row.position,
    userId: row.user_id,
    name: `${row.name} ${row.surname ?? ""}`.trim(),
    status: toSignerStatus(row.status),
    signedAt: row.signed_at,
    method: row.signing_method,
    isYou: currentUserId != null && row.user_id === currentUserId,
  };
}

/**
 * A finished container's signatures, read as a chain.
 *
 * `document_signatures` records no position — it is an audit trail, not the
 * chain — so every row is given position 1. That is honest rather than
 * decorative: the order the chain actually ran in is not recoverable from these
 * rows, and inventing 1..n would make every multi-signature container report
 * "signing in order" (`isOrderedChain`) even when it was one parallel group.
 * Nothing is pending on a finished container, so position carries no other
 * behaviour here.
 */
export function toContainerSigners(
  rows: SignatureRow[],
  currentUserId: number | null
): ChainSigner[] {
  return rows
    .map((row) => ({
      position: 1,
      userId: row.signer_user_id,
      name: row.signer_name?.trim() ?? "",
      status: "signed" as const,
      signedAt: row.signed_at,
      method: row.method,
      isYou: currentUserId != null && row.signer_user_id === currentUserId,
    }))
    .sort((a, b) => parseTimeOrZero(a.signedAt ?? "") - parseTimeOrZero(b.signedAt ?? ""));
}

/** Finished signed containers for one project of a building, newest first. */
export async function listSignedContainers(
  buildingId: number,
  projectId: number | null,
  currentUserId: number | null
): Promise<SigningListItem[]> {
  const base = supabase
    .from("documents")
    .select(CONTAINER_COLUMNS)
    .eq("building_id", buildingId)
    .eq("source", "signing")
    .eq("is_deleted", false)
    // Containers are inserted via create_document_with_versioning, so re-signing
    // the same container name versions the row — only the latest is current.
    // NULL counts as latest (nullable column; matches IfcModelStore's reading)
    // so rows written without the flag don't silently vanish from this page.
    .or("is_latest_version.is.null,is_latest_version.eq.true")
    .eq("hidden", false);

  // PostgREST renders a JS `null` as the literal `eq.null`, which never matches
  // SQL NULL — the project-less scope has to go through `is`.
  const scoped =
    projectId === null ? base.is("project_id", null) : base.or(projectScopeFilter(projectId));

  const { data, error } = await scoped
    .order("created_at", { ascending: false })
    .limit(SIGNING_LIST_LIMIT);

  if (error) throw new Error(`Failed to list signed containers: ${error.message}`);

  return ((data ?? []) as unknown as ContainerRow[]).map((row) => ({
    key: `doc-${row.id}`,
    kind: "container" as const,
    title: row.filename,
    status: "signed" as const,
    createdAt: row.created_at ?? "",
    expiresAt: null,
    senderUserId: row.created_by ?? null,
    senderName: fullName(row.user_profiles, row.created_by_name),
    size: row.size ?? null,
    documentId: row.id,
    sessionId: null,
    filePath: row.file_path,
    contentType: row.content_type ?? null,
    documentState: row.state ?? null,
    unfiled: row.project_id == null,
    projectId: row.project_id,
    verifiedAt: row.verified_at ?? null,
    verifiedByName: row.verified_at ? fullName(row.verified_by_profile, null) || null : null,
    signers: toContainerSigners(row.document_signatures ?? [], currentUserId),
    sourceDocumentIds: [],
    errorMessage: null,
  }));
}

/**
 * Signing chains in flight (or ended without a container) for one project of a
 * building.
 *
 * There is deliberately NO `.eq('user_id', currentUserId)`: chaining widened
 * the `signing_sessions` SELECT policy from "the sender" to "the sender, any
 * signer, and whoever can manage the project", and that policy IS the scope.
 * Filtering to own rows on top of it would hide exactly the chains this feature
 * exists for — the ones somebody else sent you. `currentUserId` now only marks
 * which signer is you.
 */
export async function listSigningSessions(
  buildingId: number,
  projectId: number | null,
  currentUserId: number | null
): Promise<SigningListItem[]> {
  const base = supabase
    .from("signing_sessions")
    .select(SESSION_COLUMNS)
    .eq("building_id", buildingId)
    // Excluded IN the query, not client-side: a post-limit filter would shrink
    // a truncated page below the cap and isSigningListTruncated could no longer
    // tell. `completed` chains are listed as their container; a `creating` one
    // is a half-written row that may never reach Dokobit at all (the backend's
    // sweep fails it after 15 minutes), so it is not yet a thing that happened.
    .neq("status", "completed")
    .neq("status", "creating");

  const scoped =
    projectId === null ? base.is("project_id", null) : base.or(projectScopeFilter(projectId));

  const { data, error } = await scoped
    .order("created_at", { ascending: false })
    .limit(SIGNING_LIST_LIMIT);

  if (error) throw new Error(`Failed to list signing sessions: ${error.message}`);

  return ((data ?? []) as unknown as SessionRow[]).map((row) => {
    const signers = (row.signing_session_signers ?? [])
      .map((signer) => toChainSigner(signer, currentUserId))
      .sort(byPositionThenName);
    return {
      key: `session-${row.id}`,
      kind: "session" as const,
      title: row.container_name,
      // The viewer's identity is part of the status: a legacy chain with no
      // signer rows waits on its SENDER, and under the widened SELECT policy
      // the viewer is often not them.
      status: sessionItemStatus(
        row.status,
        row.expires_at,
        new Date(),
        signers,
        currentUserId != null && row.user_id === currentUserId
      ),
      createdAt: row.created_at,
      expiresAt: row.expires_at,
      senderUserId: row.user_id,
      senderName: fullName(row.user_profiles, null),
      size: null,
      documentId: null,
      sessionId: row.id,
      filePath: null,
      contentType: null,
      documentState: null,
      unfiled: row.project_id == null,
      projectId: row.project_id,
      verifiedAt: null,
      verifiedByName: null,
      signers,
      sourceDocumentIds: row.source_doc_ids ?? [],
      errorMessage: row.error_message,
    };
  });
}

/** The source documents that went into a signed container. */
export async function listContainerContents(containerDocumentId: number): Promise<SigningFile[]> {
  const { data, error } = await supabase
    .from("signed_container_sources")
    .select("source_document_id")
    .eq("container_document_id", containerDocumentId);

  if (error) throw new Error(`Failed to list container contents: ${error.message}`);

  const ids = (data ?? []).map((row: { source_document_id: number }) => row.source_document_id);
  return listDocumentsByIds(ids);
}

/**
 * Document metadata for a set of ids, in the order the DB returns them.
 * Soft-deleted documents are dropped — a source file deleted after signing must
 * not render as a live, downloadable row (the .asice container keeps the full
 * signed content regardless).
 *
 * An EMPTY result is legitimate, not a failure: `signed_container_sources`
 * requires visibility of each source document as well as of the container, so
 * an invited signer with no other access to the source folder sees the
 * container with no contents listed. They already saw the files in Dokobit's
 * own iframe.
 */
export async function listDocumentsByIds(ids: number[]): Promise<SigningFile[]> {
  if (ids.length === 0) return [];

  const { data, error } = await supabase
    .from("documents")
    .select("id, filename, size, file_path, content_type, state, created_at, created_by_name")
    .in("id", ids)
    .eq("is_deleted", false)
    .order("filename", { ascending: true });

  if (error) throw new Error(`Failed to load documents: ${error.message}`);

  return (data ?? []).map(
    (row: {
      id: number;
      filename: string;
      size: number | null;
      file_path: string | null;
      content_type: string | null;
      state: string | null;
      created_at: string | null;
      created_by_name: string | null;
    }) => ({
      id: row.id,
      filename: row.filename,
      size: row.size ?? 0,
      filePath: row.file_path,
      contentType: row.content_type ?? null,
      state: row.state ?? null,
      createdAt: row.created_at ?? null,
      uploadedBy: row.created_by_name?.trim() ? row.created_by_name.trim() : null,
    })
  );
}
